import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert
} from '@mui/material';
import {
  Security,
  Person,
  History,
  Settings
} from '@mui/icons-material';
import { useAppSelector } from '../store/hooks';
import EmergencyControlPanel from '../components/Emergency/EmergencyControlPanel';
import UserIPManagement from '../components/Emergency/UserIPManagement';
import EmergencyLogs from '../components/Emergency/EmergencyLogs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`emergency-tabpanel-${index}`}
      aria-labelledby={`emergency-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `emergency-tab-${index}`,
    'aria-controls': `emergency-tabpanel-${index}`,
  };
}

const EmergencyPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const { user } = useAppSelector(state => state.auth);

  // Check if user has admin permissions
  const isAdmin = user?.is_superuser || 
                  user?.roles?.includes('admin') || 
                  user?.roles?.includes('superuser');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access Denied: You do not have permission to access the Emergency Security System.
          Only administrators can access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Security sx={{ mr: 2, fontSize: 32, color: 'error.main' }} />
          <Typography variant="h4" component="h1">
            Emergency Security System
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Monitor and control system security with emergency response capabilities.
          All actions are logged and require proper authorization.
        </Typography>
      </Box>

      {/* Emergency Alert */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="subtitle2">⚠️ Emergency System Active</Typography>
        <Typography variant="body2">
          This system provides immediate security controls. Use responsibly and ensure all actions are properly documented.
          Emergency actions can affect all users and system functionality.
        </Typography>
      </Alert>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            aria-label="emergency management tabs"
          >
            <Tab
              icon={<Security />}
              label="Control Panel"
              {...a11yProps(0)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<Person />}
              label="User & IP Management"
              {...a11yProps(1)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<History />}
              label="Logs & Incidents"
              {...a11yProps(2)}
              sx={{ minHeight: 72 }}
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={currentTab} index={0}>
        <EmergencyControlPanel />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <UserIPManagement />
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <EmergencyLogs />
      </TabPanel>
    </Container>
  );
};

export default EmergencyPage; 